import { useEffect, useMemo, useRef, type RefObject } from 'react';
import type L from 'leaflet';
import { useTheme } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useAuthStore } from '../../store/authStore';
import { usePlanEventsStore } from '../../store/planEventsStore';
import {
  getLeaflet,
  PLAN_FOCUS_CLASS,
  DEFENSE_PLAN_BEACON_SELECTED_CLASS,
  DEFENSE_PLAN_BEACON_HOVERED_CLASS,
} from './leafletHelpers';
import { featureBounds, planBounds } from '../../utils/planGeometry';
import {
  DEFENSE_STRATEGIES,
  PLAN_CASING,
  PLAN_CASING_OPACITY,
  PLAN_LINE_WEIGHT,
  PLAN_CASING_EXTRA_WEIGHT,
  PLAN_AREA_FILL_OPACITY,
  strategyBadgeColors,
} from './defenseStrategies';
import { planFitPadding } from './planFitPadding';
import { hoverStyleFor, diffHoverIds } from './planFeatureStyle';
import { drawStrokeIn, drawAreaIn, popIn, prefersReducedMotion } from './planDrawIn';
import { takeRecentlyAddedPlanFeature } from '../../utils/recentPlanFeatures';
import { isRangeClick, isToggleClick, planSelectedIdSet } from '../../utils/planSelection';
import type {
  DefensePlan,
  DefensePlanFeature,
  DefenseFeatureKind,
  DefenseStrategy,
  PlanBounds,
} from '../../types';

// Shared with useDefensePlanDraw.web.ts, which draws its in-progress preview
// line in the same pane so a line being drawn sits at the same depth as a
// committed one. z 450: above overlayPane (400, service-area outlines) and
// the hazard fills (fhszPane 350 / fireHistoryPane 360), below shadowPane
// (500) and markerPane (600) — plan lines read over the backdrop layers but
// a drawn feature never covers a pin.
export const DEFENSE_PLAN_PANE = 'defensePlanPane';
const DEFENSE_PLAN_PANE_Z_INDEX = '450';

// A beacon sits at its feature's centre, which for an area feature is
// inside the polygon's own (semi-transparent, but still interactive) fill —
// so a beacon sharing DEFENSE_PLAN_PANE with the shapes has to out-rank the
// polygon's own SVG <path> in hit-testing, and a marker's per-instance
// inline z-index (Leaflet computes it from screen Y for its marker-sorting
// feature) turned out not to reliably beat that path's default stacking:
// a beacon click was landing on the shape underneath and hitting its
// (owner-gated) click handler instead, doing nothing for a read-only
// viewer. A second pane, definitively stacked above the first, sidesteps
// the ambiguity — pane z-index is unambiguous sibling-div stacking, not a
// per-element tug of war.
export const DEFENSE_PLAN_BEACON_PANE = 'defensePlanBeaconPane';
const DEFENSE_PLAN_BEACON_PANE_Z_INDEX = '460';

// Applied to every committed feature's coloured layer (the casing underneath
// does not get it) so both the audit and DEFENSE_PLAN_CSS's pointer-events
// rule in leafletHelpers.ts can target it.
export const DEFENSE_PLAN_FEATURE_CLASS = 'leaflet-defense-plan';

// Created lazily by whichever of the two defense-plan hooks (this one or
// useDefensePlanDraw.web.ts) runs first, so neither has to assume the other
// already ran.
export function ensureDefensePlanPane(map: L.Map): void {
  if (!map.getPane(DEFENSE_PLAN_PANE)) {
    map.createPane(DEFENSE_PLAN_PANE).style.zIndex = DEFENSE_PLAN_PANE_Z_INDEX;
  }
  if (!map.getPane(DEFENSE_PLAN_BEACON_PANE)) {
    map.createPane(DEFENSE_PLAN_BEACON_PANE).style.zIndex = DEFENSE_PLAN_BEACON_PANE_Z_INDEX;
  }
}

// The selected feature gets extra weight/radius so it reads as picked without
// changing its colour (colour already carries the strategy); a hovered one
// gets a smaller step. See planFeatureStyle.ts.
const POINT_RADIUS = 6;
const POINT_RING_WEIGHT = 2;

// A home feature (kind 'point' + structureId — see DefensePlanFeatureSchema's
// comment) draws its usual small filled dot at the structure's centroid, plus
// an unfilled ring in the strategy's own color around it, so it reads as "on
// a house" rather than just another point. Several strategies applied to the
// same house (Glenn's ask — active defense, paint, hardening, each its own
// feature) each get their own ring, nested at an increasing radius
// (homeRingIndex below) rather than one ring per house, so every strategy on
// a home stays individually visible instead of collapsing into one mark.
// Radii are circleMarker pixel radii (constant on screen at any zoom), not
// L.circle's meter radii.
const HOME_RING_BASE_OFFSET_PX = 5;
const HOME_RING_STEP_PX = 4;
const HOME_RING_WEIGHT = 2;

// A line or area feature whose on-screen bounding box is smaller than this
// (in CSS pixels, on its longer side) gets a beacon: at that size the shape
// itself is unreadable — a fire break spanning a couple of city blocks can
// draw down to a handful of pixels once the map is fit to a whole plan a
// few kilometres across. Point features never qualify (see buildEntry) —
// a circleMarker's on-screen size doesn't shrink with zoom, so it's never
// the thing this problem is about.
//
// 44, not the ~28 a shape this size (BEACON_SIZE_PX) would suggest: Glenn's
// seeded demo plan's smallest feature (a ~300m brush-clearing rectangle)
// measures 34-38px at the zoom fitBounds settles on for the whole plan —
// already hard to click precisely, and exactly the case this exists for —
// but the docked panel's fit padding leaves it just over a literal 28px.
// 44 covers it with room, while still being well under a feature anyone
// would call "readable" (a hundred-plus px).
const BEACON_MAX_SIDE_PX = 44;
const BEACON_SIZE_PX = 24;
const BEACON_DOT_PX = 8;
const BEACON_RING_WIDTH_PX = 2.5;

interface FeatureEntry {
  group: L.LayerGroup;
  // The layer that carries the strategy colour and takes the click — the
  // casing polyline underneath a line is neither.
  main: L.Polyline | L.CircleMarker | L.Polygon;
  kind: DefenseFeatureKind;
  strategy: DefenseStrategy;
  // null for a point feature (see buildEntry) or a plain marker for a line
  // or area — added to / removed from the map by updateBeaconVisibility as
  // the shape crosses BEACON_MAX_SIDE_PX, never left permanently mounted so
  // a hidden one can't still take clicks.
  beacon: L.Marker | null;
  beaconBounds: PlanBounds | null;
  beaconVisible: boolean;
  // aria-label text for the beacon, applied in updateBeaconVisibility —
  // Leaflet only creates a marker's DOM element on addTo (Marker.onAdd ->
  // _initIcon), so setting this any earlier, before the beacon has ever
  // been shown, would silently do nothing.
  beaconLabel: string | null;
  // Mirrors whether this feature is the selected one — kept alongside the
  // DOM class toggle (setBeaconSelected) because a beacon's element is torn
  // down and recreated every time updateBeaconVisibility hides/reshows it,
  // dropping any class that was only ever set on the old element. Re-added
  // in updateBeaconVisibility itself, from this field, not from whatever
  // the icon was built with.
  selected: boolean;
  // Pointer is over this feature's row (or the header of its bundle) or its
  // shape. Kept here for the same reason as `selected`: a beacon's element
  // is recreated on every show, so the class is re-applied from this field.
  hovered: boolean;
  // The feature's bundle at the time this entry was built, or null when
  // unbundled — read by the bundle-selection highlight effect below without
  // having to look the feature back up in `renderedPlan`. Also part of the
  // diff effect's rebuild test (see the entries loop), so a feature moving
  // in or out of a bundle gets its tooltip re-bound even when its strategy
  // hasn't changed.
  bundleId: string | null;
}

// " · <bundle name>" appended to a bundled feature's tooltip/beacon label
// (Sept 16 2026 — Glenn's "bundle steps" ask), else the label unchanged.
function withBundleSuffix(
  label: string,
  feature: DefensePlanFeature,
  bundles: DefensePlan['bundles']
): string {
  if (!feature.bundleId) return label;
  const bundle = bundles.find((b) => b.id === feature.bundleId);
  return bundle ? `${label} · ${bundle.name}` : label;
}

// Small filled circle with a ring in the strategy's stroke colour — casing
// white (PLAN_CASING), same reasoning as the line casing: a dark stroke
// needs to survive the satellite basemap regardless of app theme. Bumped a
// touch and its ring/dot brightened while selected, the same "reads as
// picked" language setHighlight gives the shapes, but through setIcon
// rather than setStyle (a DivIcon marker has no vector style to touch).
function makeBeaconIcon(L: typeof import('leaflet'), color: string, selected: boolean): L.DivIcon {
  const size = selected ? BEACON_SIZE_PX + 4 : BEACON_SIZE_PX;
  const dot = selected ? BEACON_DOT_PX + 2 : BEACON_DOT_PX;
  return L.divIcon({
    className: 'leaflet-defense-plan-beacon',
    html:
      `<div style="width:${size}px;height:${size}px;border-radius:50%;` +
      `background:${PLAN_CASING};border:${BEACON_RING_WIDTH_PX}px solid ${color};` +
      `box-shadow:0 1px 3px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;">` +
      `<div style="width:${dot}px;height:${dot}px;border-radius:50%;background:${color};"></div></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// Projects a feature's lat/lng bounding box onto the current view and shows
// or hides its beacon depending on whether the box still reads on screen.
// Pulled out of the diff effect so the zoomend listener (registered once,
// independent of plan changes) can call it with whatever's in `entries` at
// the time, not just right after a rebuild.
function updateBeaconVisibility(map: L.Map, entries: Map<string, FeatureEntry>): void {
  for (const [featureId, entry] of entries) {
    if (!entry.beacon || !entry.beaconBounds) continue;
    const { n, s, e, w } = entry.beaconBounds;
    const p1 = map.latLngToContainerPoint([n, w]);
    const p2 = map.latLngToContainerPoint([s, e]);
    const maxSide = Math.max(Math.abs(p2.x - p1.x), Math.abs(p2.y - p1.y));
    const shouldShow = maxSide < BEACON_MAX_SIDE_PX;
    if (shouldShow === entry.beaconVisible) continue;
    entry.beaconVisible = shouldShow;
    if (shouldShow) {
      entry.beacon.addTo(map);
      // Only exists from here — Leaflet creates a marker's DOM element on
      // addTo (Marker.onAdd -> _initIcon), and a fresh one each time (torn
      // down by the remove() below), so both attributes and the selected
      // class get re-applied on every show rather than trusted from before.
      const el = entry.beacon.getElement();
      el?.setAttribute('data-feature-id', featureId);
      if (entry.beaconLabel) el?.setAttribute('aria-label', entry.beaconLabel);
      el?.classList.toggle(DEFENSE_PLAN_BEACON_SELECTED_CLASS, entry.selected);
      el?.classList.toggle(DEFENSE_PLAN_BEACON_HOVERED_CLASS, entry.hovered);
    } else {
      entry.beacon.remove();
    }
  }
}

function setHighlight(entry: FeatureEntry, highlighted: boolean): void {
  const state = { selected: highlighted, hovered: entry.hovered };
  if (entry.kind === 'point') {
    const marker = entry.main as L.CircleMarker;
    const next = hoverStyleFor({ weight: POINT_RING_WEIGHT, radius: POINT_RADIUS }, state);
    marker.setStyle({ weight: next.weight });
    marker.setRadius(next.radius ?? POINT_RADIUS);
  } else {
    entry.main.setStyle({ weight: hoverStyleFor({ weight: PLAN_LINE_WEIGHT }, state).weight });
  }
}

function setHovered(entry: FeatureEntry, hovered: boolean): void {
  if (entry.hovered === hovered) return;
  entry.hovered = hovered;
  setHighlight(entry, entry.selected);
  entry.beacon?.getElement()?.classList.toggle(DEFENSE_PLAN_BEACON_HOVERED_CLASS, hovered);
}

/**
 * Renders the map's one visible defense plan: `viewedPlan` when
 * `uiStore.viewedPlanId` is set, else `ownPlan` while the panel is open,
 * else nothing. This is the rendering rule shared with the panel and the
 * legend (DefensePlanPanel.web.tsx, MapLegend.tsx), who read the same
 * three-way choice for their own display.
 *
 * Owns the `subscribeViewedPlan`/`unsubscribeViewedPlan` lifecycle, keyed on
 * `viewedPlanId` alone — that id is the one thing about the current view
 * that decides whether the subscription should be open at all.
 *
 * Shaped like useFireHistoryOverlay: one pane, built lazily. It diverges
 * after that because a defense plan is live per-user data, not a static
 * per-area export — features are diffed and added/removed individually by
 * id instead of the whole pane being toggled en masse.
 */
export function useDefensePlanLayer({
  mapRef,
  isLoaded,
}: {
  mapRef: RefObject<L.Map | null>;
  isLoaded: boolean;
}): void {
  const viewedPlanId = useUIStore((s) => s.viewedPlanId);
  const planPanelOpen = useUIStore((s) => s.planPanelOpen);
  const selectedFeatureId = useUIStore((s) => s.selectedPlanFeatureId);
  const multiSelection = useUIStore((s) => s.planMultiSelection);
  const viewedPlan = useDefensePlansStore((s) => s.viewedPlan);
  const ownPlan = useDefensePlansStore((s) => s.ownPlan);
  // True once the active own plan's doc actually exists server-side — false
  // for the brief window between the optimistic create (addFeature's first
  // feature, or duplicateActivePlan) and that write resolving. `ownPlan?.id`
  // alone can't gate the parties/assignments subscription below: it's the
  // same id before and after the write lands, so nothing would re-trigger
  // the effect once persistence actually happens. See
  // defensePlansStore.unpersistedPlanIds's own comment for the rules-side
  // reason (a missing parent doc denies subcollection reads).
  const ownPlanPersisted = useDefensePlansStore((s) =>
    s.ownPlan ? !s.unpersistedPlanIds[s.ownPlan.id] : false
  );
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // Beacon ring/dot colour: reuses the same dark-theme lighten the strategy
  // badges use (strategyBadgeColors), not a satellite/standard basemap
  // check — there's no per-basemap theme hook, and the badge's "too dark to
  // read against a dark surface" test is the same problem a beacon has
  // against a dark map.
  const { isDark } = useTheme();

  const renderedPlan: DefensePlan | null = viewedPlanId
    ? viewedPlan
    : planPanelOpen
      ? ownPlan
      : null;
  const isOwn = !!renderedPlan && renderedPlan.ownerId === userId;

  const entriesRef = useRef<Map<string, FeatureEntry>>(new Map());
  // Ids currently drawn as hovered: the hovered feature, or every member of
  // the hovered bundle. The hover effect diffs against it.
  const hoveredIdsRef = useRef<Set<string>>(new Set());
  // Read at click time inside a Leaflet event handler registered once per
  // feature, so it has to be a ref rather than a value closed over at build
  // time — the same convention useMarkerLayer's selectedIdRef follows.
  const isOwnRef = useRef(isOwn);
  isOwnRef.current = isOwn;
  // The anchor plus the multi-selection, as one Set built once per change.
  const selectedIds = useMemo(
    () => planSelectedIdSet(selectedFeatureId, multiSelection),
    [selectedFeatureId, multiSelection]
  );
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  useEffect(() => {
    const store = useDefensePlansStore.getState();
    if (viewedPlanId) {
      store.subscribeViewedPlan(viewedPlanId);
    } else {
      store.unsubscribeViewedPlan();
    }
    return () => {
      if (viewedPlanId) useDefensePlansStore.getState().unsubscribeViewedPlan();
    };
  }, [viewedPlanId]);

  // "Who's responsible" for the viewed plan (parties + their links to
  // features), same lifecycle as the plan subscription above — the viewer
  // panel/card need it whenever a plan is being shown at all, not only
  // while a row is on screen.
  useEffect(() => {
    const store = useDefensePlansStore.getState();
    if (viewedPlanId) {
      store.subscribeViewedPlanParties(viewedPlanId);
      store.subscribeViewedPlanAssignments(viewedPlanId);
    } else {
      store.unsubscribeViewedPlanParties();
      store.unsubscribeViewedPlanAssignments();
    }
    return () => {
      if (viewedPlanId) {
        useDefensePlansStore.getState().unsubscribeViewedPlanParties();
        useDefensePlansStore.getState().unsubscribeViewedPlanAssignments();
      }
    };
  }, [viewedPlanId]);

  // "Who's responsible" for the owner's own plan, while the editor is open —
  // the panel needs it to render each row's who line, the "People and
  // organizations" section, and the header summary. Keyed on ownPlan.id
  // (not planPanelOpen alone) so drawing the very first feature, which
  // mints the plan doc, picks the subscription up as soon as there's a
  // plan id to subscribe to — but gated on `ownPlanPersisted` too: the doc
  // is only optimistic (not yet written) for the first tick after that
  // first feature, and subscribing before the write lands throws
  // "Missing or insufficient permissions" (rules deny reads under a missing
  // parent). This effect re-runs once `ownPlanPersisted` flips true, even
  // though `ownPlan.id` itself doesn't change across that write.
  useEffect(() => {
    const store = useDefensePlansStore.getState();
    if (planPanelOpen && ownPlan?.id && ownPlanPersisted) {
      store.subscribeOwnPlanParties(ownPlan.id);
      store.subscribeOwnPlanAssignments(ownPlan.id);
      // Reported progress (Sept 2026 update loop) — same gating as the two
      // subscriptions above: the panel's "New updates (n)" affordance and
      // PlanUpdatesSheet both need it only while the owner's editor is open
      // on a real (persisted) plan.
      store.subscribeOwnPlanAssignmentUpdates(ownPlan.id);
      // Vendor bids (Oct 2026): owner-read only, under the plan doc, so the
      // same wait for the plan to be written applies.
      store.subscribeBids(ownPlan.id);
      store.subscribeFundingRequests(ownPlan.id, ownPlan.ownerId);
    } else {
      store.unsubscribeOwnPlanParties();
      store.unsubscribeOwnPlanAssignments();
      store.unsubscribeOwnPlanAssignmentUpdates();
      store.unsubscribeBids();
      store.unsubscribeFundingRequests();
    }
    return () => {
      if (planPanelOpen && ownPlan?.id && ownPlanPersisted) {
        useDefensePlansStore.getState().unsubscribeOwnPlanParties();
        useDefensePlansStore.getState().unsubscribeOwnPlanAssignments();
        useDefensePlansStore.getState().unsubscribeOwnPlanAssignmentUpdates();
        useDefensePlansStore.getState().unsubscribeBids();
        useDefensePlansStore.getState().unsubscribeFundingRequests();
      }
    };
  }, [planPanelOpen, ownPlan?.id, ownPlan?.ownerId, ownPlanPersisted]);

  // Events scheduled on a bundle (Oct 2026), same lifecycles as the parties
  // above: the viewer shows the next one under each bundle, and the editor
  // lists them and keeps a bundle that still has one from being pruned. Events
  // are world-readable and do not live under the plan doc, so unlike the
  // subscriptions above there is nothing to wait for before opening them.
  useEffect(() => {
    const store = usePlanEventsStore.getState();
    if (viewedPlanId) store.subscribeViewedPlanEvents(viewedPlanId);
    else store.unsubscribeViewedPlanEvents();
    return () => {
      if (viewedPlanId) usePlanEventsStore.getState().unsubscribeViewedPlanEvents();
    };
  }, [viewedPlanId]);

  useEffect(() => {
    const store = usePlanEventsStore.getState();
    if (planPanelOpen && ownPlan?.id) store.subscribeOwnPlanEvents(ownPlan.id);
    else store.unsubscribeOwnPlanEvents();
    return () => {
      if (planPanelOpen && ownPlan?.id) {
        usePlanEventsStore.getState().unsubscribeOwnPlanEvents();
      }
    };
  }, [planPanelOpen, ownPlan?.id]);

  // While a plan is being viewed or the editor is open, pins step back
  // (leafletHelpers.PLAN_FOCUS_CSS: faded + the plan pane raised above the
  // marker pane) so a small feature doesn't hide under a pin sitting on top
  // of it. Toggled on the map container the same way
  // useDefensePlanDraw.web.ts toggles DEFENSE_PLAN_DRAWING_CLASS.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;
    const container = map.getContainer();
    container.classList.toggle(PLAN_FOCUS_CLASS, !!viewedPlanId || planPanelOpen);
    return () => {
      container.classList.remove(PLAN_FOCUS_CLASS);
    };
  }, [viewedPlanId, planPanelOpen, isLoaded, mapRef]);

  // Diff the rendered plan's features by id: add layer groups for new ones,
  // remove groups for ones that dropped out, and rebuild any whose cached
  // strategy no longer matches the feature (features are otherwise
  // immutable once drawn — there's no edit-strategy action today — but this
  // keeps the diff honest rather than assuming that never changes).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;
    ensureDefensePlanPane(map);
    const entries = entriesRef.current;

    if (!renderedPlan) {
      for (const entry of entries.values()) {
        entry.group.remove();
        entry.beacon?.remove();
      }
      entries.clear();
      return;
    }

    const L = getLeaflet();
    const onSelect = (featureId: string, original?: MouseEvent) => {
      if (!isOwnRef.current) return;
      const ui = useUIStore.getState();
      // Bundle picker open (DefensePlanPanel's "Bundle" mode): a click on a
      // plan feature toggles its membership in the selection instead of the
      // usual row-highlight — see DefensePlanPanel.web.tsx's Bundle button.
      // Never reached while a draw tool is armed: starting the picker
      // disarms one and arming one closes the picker (uiStore's
      // startPlanBundleSelection/setPlanDrawTool).
      if (ui.planBundleSelection) {
        ui.togglePlanBundleSelectionMember(featureId);
        return;
      }
      // Cmd/Ctrl/Shift+click builds a multi-selection. The map has no row
      // order to range over, so Shift toggles too.
      if (isToggleClick(original) || isRangeClick(original)) {
        ui.togglePlanMultiSelection(featureId);
        return;
      }
      ui.setSelectedPlanFeature(featureId);
    };
    const onHover = (featureId: string) => useUIStore.getState().setHoveredPlanFeature(featureId);
    const onHoverEnd = (featureId: string) =>
      useUIStore.getState().clearHoveredPlanFeature(featureId);
    // Right click on a shape or beacon: select it (or keep a multi-selection
    // that already holds it) and open the context menu at the cursor.
    // Stopped here so Leaflet's own map-level contextmenu never fires, and
    // the browser's menu stays away. Idle while a picker, a draw tool or a
    // list drag owns the pointer.
    const onContext = (featureId: string, e: L.LeafletMouseEvent) => {
      e.originalEvent.preventDefault();
      L.DomEvent.stop(e);
      if (!isOwnRef.current) return;
      const ui = useUIStore.getState();
      // An armed tool does not block the menu (the tool stays armed after a
      // point commits, and that is when a fresh shape gets copied or
      // bundled, Oct 14 2026 browser check); only a stroke with vertices
      // down does, since Escape and Enter belong to the stroke then.
      if (ui.planBundleSelection || ui.planStrokeActive || ui.planListDrag) return;
      const held = selectedIdsRef.current;
      if (!(held.size >= 2 && held.has(featureId))) ui.setSelectedPlanFeature(featureId);
      ui.openPlanFeatureContextMenu({
        featureId,
        x: e.originalEvent.clientX,
        y: e.originalEvent.clientY,
      });
    };
    // A beacon click always selects and focuses, regardless of ownership —
    // it stands in for a list row (DefensePlanFeatureRow's onSelect), which
    // works the same way in the read-only viewer as it does in the editor.
    // Unlike onSelect above, this is not gated on isOwnRef.
    const onBeaconSelect = (featureId: string, original?: MouseEvent) => {
      if (isOwnRef.current && (isToggleClick(original) || isRangeClick(original))) {
        useUIStore.getState().togglePlanMultiSelection(featureId);
        return;
      }
      useUIStore.getState().setSelectedPlanFeature(featureId);
      useUIStore.getState().requestPlanFeatureFocus(featureId);
    };

    // Only line/area features get a beacon. A point's circleMarker is a
    // fixed on-screen size (POINT_RADIUS) that never shrinks with zoom, so
    // it never hits the "can't read it" problem a beacon solves — stacking
    // one on top would just be a second, redundant dot at the same spot.
    const buildBeacon = (feature: DefensePlanFeature): L.Marker => {
      const bounds = featureBounds(feature);
      const center: [number, number] = [(bounds.n + bounds.s) / 2, (bounds.e + bounds.w) / 2];
      const color = strategyBadgeColors(feature.strategy, isDark).icon;
      const beacon = L.marker(center, {
        pane: DEFENSE_PLAN_BEACON_PANE,
        icon: makeBeaconIcon(L, color, selectedIdsRef.current.has(feature.id)),
        keyboard: false,
      });
      beacon.on('click', (e) => onBeaconSelect(feature.id, e.originalEvent));
      beacon.on('contextmenu', (e) => onContext(feature.id, e));
      beacon.on('mouseover', () => useUIStore.getState().setHoveredPlanFeature(feature.id));
      beacon.on('mouseout', () => useUIStore.getState().clearHoveredPlanFeature(feature.id));
      return beacon;
    };
    const bundles = renderedPlan.bundles;

    const buildEntry = (feature: DefensePlanFeature): FeatureEntry => {
      const def = DEFENSE_STRATEGIES[feature.strategy];
      if (feature.kind === 'point') {
        const marker = L.circleMarker(feature.coords[0], {
          pane: DEFENSE_PLAN_PANE,
          radius: POINT_RADIUS,
          color: PLAN_CASING,
          weight: POINT_RING_WEIGHT,
          fillColor: def.stroke,
          fillOpacity: 1,
          className: DEFENSE_PLAN_FEATURE_CLASS,
        });
        marker.on('click', (e) => onSelect(feature.id, e.originalEvent));
        marker.on('contextmenu', (e) => onContext(feature.id, e));
        marker.on('mouseover', () => onHover(feature.id));
        marker.on('mouseout', () => onHoverEnd(feature.id));
        // Sticky (follows the cursor rather than pinning to the shape) so a
        // long label on a small point still reads instead of clipping
        // against the map edge. Bound on the interactive layer itself, same
        // as click — the casing (line/area have one, a point doesn't) never
        // gets one since it's interactive: false and would never fire. A
        // home feature's tooltip names the house, not just the strategy —
        // the dot alone can't (several strategies draw the same small dot).
        const tooltipLabel = withBundleSuffix(
          feature.structureId && feature.address ? `${def.label} · ${feature.address}` : def.label,
          feature,
          bundles
        );
        marker.bindTooltip(tooltipLabel, { sticky: true, direction: 'top' });
        const layers: (L.Polyline | L.CircleMarker | L.Polygon)[] = [marker];
        // Home feature: an unfilled ring in the strategy color, non-
        // interactive (clicks and the tooltip stay on the dot) so several
        // strategies on the same house nest as concentric rings instead of
        // one dot swallowing another — see HOME_RING_* above.
        if (feature.structureId) {
          const ringIndex = homeRingIndex.get(feature.id) ?? 0;
          layers.push(
            L.circleMarker(feature.coords[0], {
              pane: DEFENSE_PLAN_PANE,
              radius: POINT_RADIUS + HOME_RING_BASE_OFFSET_PX + ringIndex * HOME_RING_STEP_PX,
              color: def.stroke,
              weight: HOME_RING_WEIGHT,
              fill: false,
              interactive: false,
            })
          );
        }
        const group = L.layerGroup(layers).addTo(map);
        // Only exists once the marker has actually been added to the map —
        // that's what creates its SVG element.
        marker.getElement()?.setAttribute('data-feature-id', feature.id);
        return {
          group,
          main: marker,
          kind: 'point',
          strategy: feature.strategy,
          beacon: null,
          beaconBounds: null,
          beaconVisible: false,
          beaconLabel: null,
          selected: selectedIdsRef.current.has(feature.id),
          hovered: false,
          bundleId: feature.bundleId ?? null,
        };
      }
      if (feature.kind === 'area') {
        // No casing (see the comment on PLAN_AREA_FILL_OPACITY): a
        // low-opacity fill of the strategy colour reads against the
        // satellite basemap on its own.
        const area = L.polygon(feature.coords, {
          pane: DEFENSE_PLAN_PANE,
          color: def.stroke,
          weight: PLAN_LINE_WEIGHT,
          fillColor: def.stroke,
          fillOpacity: PLAN_AREA_FILL_OPACITY,
          className: DEFENSE_PLAN_FEATURE_CLASS,
        });
        area.on('click', (e) => onSelect(feature.id, e.originalEvent));
        area.on('contextmenu', (e) => onContext(feature.id, e));
        area.on('mouseover', () => onHover(feature.id));
        area.on('mouseout', () => onHoverEnd(feature.id));
        const areaLabel = withBundleSuffix(def.label, feature, bundles);
        area.bindTooltip(areaLabel, { sticky: true, direction: 'top' });
        const group = L.layerGroup([area]).addTo(map);
        area.getElement()?.setAttribute('data-feature-id', feature.id);
        return {
          group,
          main: area,
          kind: 'area',
          strategy: feature.strategy,
          beacon: buildBeacon(feature),
          beaconBounds: featureBounds(feature),
          beaconVisible: false,
          beaconLabel: areaLabel,
          selected: selectedIdsRef.current.has(feature.id),
          hovered: false,
          bundleId: feature.bundleId ?? null,
        };
      }
      // Casing first so the coloured stroke paints over it, the same order
      // useFireHistoryOverlay uses for its perimeter outlines.
      const casing = L.polyline(feature.coords, {
        pane: DEFENSE_PLAN_PANE,
        color: PLAN_CASING,
        opacity: PLAN_CASING_OPACITY,
        weight: PLAN_LINE_WEIGHT + PLAN_CASING_EXTRA_WEIGHT,
        dashArray: def.dashArray,
        interactive: false,
      });
      const stroke = L.polyline(feature.coords, {
        pane: DEFENSE_PLAN_PANE,
        color: def.stroke,
        weight: PLAN_LINE_WEIGHT,
        dashArray: def.dashArray,
        className: DEFENSE_PLAN_FEATURE_CLASS,
      });
      stroke.on('click', (e) => onSelect(feature.id, e.originalEvent));
      stroke.on('contextmenu', (e) => onContext(feature.id, e));
      stroke.on('mouseover', () => onHover(feature.id));
      stroke.on('mouseout', () => onHoverEnd(feature.id));
      const lineLabel = withBundleSuffix(def.label, feature, bundles);
      stroke.bindTooltip(lineLabel, { sticky: true, direction: 'top' });
      const group = L.layerGroup([casing, stroke]).addTo(map);
      stroke.getElement()?.setAttribute('data-feature-id', feature.id);
      return {
        group,
        main: stroke,
        kind: 'line',
        strategy: feature.strategy,
        beacon: buildBeacon(feature),
        beaconBounds: featureBounds(feature),
        beaconVisible: false,
        beaconLabel: lineLabel,
        selected: selectedIdsRef.current.has(feature.id),
        hovered: false,
        bundleId: feature.bundleId ?? null,
      };
    };

    // Precomputed once per render pass (not inside buildEntry, which only
    // runs for new/changed features): each home feature's 0-based index
    // among every OTHER home feature on the same structure, in plan-array
    // order, so a house with several strategies stacks its rings in a
    // stable order regardless of which entries actually get rebuilt this
    // pass.
    const homeRingIndex = new Map<string, number>();
    const homeFeatureCount = new Map<string, number>();
    for (const feature of renderedPlan.features) {
      if (!feature.structureId) continue;
      const count = homeFeatureCount.get(feature.structureId) ?? 0;
      homeRingIndex.set(feature.id, count);
      homeFeatureCount.set(feature.structureId, count + 1);
    }

    // Features drawn in this session, entering now. Taken once, so a later
    // rebuild of the same feature (a strategy change) or a snapshot that
    // re-sends it can't replay the entrance.
    const enteringIds: string[] = [];
    const playEntrance = (feature: DefensePlanFeature, entry: FeatureEntry) => {
      const layers = entry.group.getLayers() as L.Path[];
      if (feature.kind === 'line') {
        const dashed = !!DEFENSE_STRATEGIES[feature.strategy].dashArray;
        for (const layer of layers) drawStrokeIn(layer, dashed);
      } else if (feature.kind === 'area') {
        for (const layer of layers) drawAreaIn(layer, PLAN_AREA_FILL_OPACITY);
      } else {
        for (const layer of layers) popIn(layer.getElement?.());
      }
      enteringIds.push(feature.id);
    };

    const nextIds = new Set<string>();
    for (const feature of renderedPlan.features) {
      nextIds.add(feature.id);
      const existing = entries.get(feature.id);
      if (
        existing &&
        existing.strategy === feature.strategy &&
        existing.bundleId === (feature.bundleId ?? null)
      )
        continue;
      if (existing) {
        existing.group.remove();
        existing.beacon?.remove();
      }
      const entry = buildEntry(feature);
      if (takeRecentlyAddedPlanFeature(feature.id, 'layer') && !prefersReducedMotion()) {
        playEntrance(feature, entry);
      }
      entry.hovered = hoveredIdsRef.current.has(feature.id);
      if (entry.hovered)
        entry.beacon?.getElement()?.classList.add(DEFENSE_PLAN_BEACON_HOVERED_CLASS);
      setHighlight(entry, selectedIdsRef.current.has(feature.id));
      entries.set(feature.id, entry);
    }

    for (const [id, entry] of entries) {
      if (!nextIds.has(id)) {
        entry.group.remove();
        entry.beacon?.remove();
        entries.delete(id);
      }
    }

    updateBeaconVisibility(map, entries);
    // A small new shape shows as a beacon; the marker element itself carries
    // Leaflet's positioning transform, so the pop goes on its inner disc.
    for (const id of enteringIds) {
      const entry = entries.get(id);
      if (entry?.beaconVisible) popIn(entry.beacon?.getElement()?.firstElementChild);
    }
  }, [renderedPlan, isLoaded, mapRef, isDark]);

  // BEACON_MAX_SIDE_PX is a screen-pixel test, so it has to be re-evaluated
  // whenever the map's scale changes — registered once (independent of plan
  // changes) rather than inside the diff effect above, since re-running the
  // whole diff on every zoom would rebuild layers that didn't change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;
    const handleZoom = () => updateBeaconVisibility(map, entriesRef.current);
    map.on('zoomend', handleZoom);
    // A pan or zoom leaves the context menu pointing at the wrong spot.
    const closeContextMenu = () => useUIStore.getState().closePlanFeatureContextMenu();
    map.on('movestart', closeContextMenu);
    map.on('zoomstart', closeContextMenu);
    return () => {
      map.off('zoomend', handleZoom);
      map.off('movestart', closeContextMenu);
      map.off('zoomstart', closeContextMenu);
    };
  }, [isLoaded, mapRef]);

  // Selection highlight, toggled without touching anything else — this is
  // the hot path for a panel row click, so it stays O(1) rather than
  // re-running the diff above. A beacon's "selected" look is a class toggle
  // (DEFENSE_PLAN_BEACON_SELECTED_CLASS, styled in leafletHelpers.ts) rather
  // than a setIcon call — cheaper, and the same shape setHighlight already
  // uses for the vector layers (setStyle, not a full rebuild).
  const setBeaconSelected = (entry: FeatureEntry, highlighted: boolean): void => {
    entry.selected = highlighted;
    entry.beacon?.getElement()?.classList.toggle(DEFENSE_PLAN_BEACON_SELECTED_CLASS, highlighted);
  };
  const prevSelectedRef = useRef<ReadonlySet<string>>(new Set());
  useEffect(() => {
    const entries = entriesRef.current;
    for (const id of prevSelectedRef.current) {
      if (selectedIds.has(id)) continue;
      const entry = entries.get(id);
      if (entry) {
        setHighlight(entry, false);
        setBeaconSelected(entry, false);
      }
    }
    for (const id of selectedIds) {
      const entry = entries.get(id);
      if (entry) {
        setHighlight(entry, true);
        setBeaconSelected(entry, true);
      }
    }
    prevSelectedRef.current = selectedIds;
  }, [selectedIds]);

  // Hover, from a list row, a bundle header or a shape. Diffs the previous
  // hovered ids against the next and restyles only those, so a pointer
  // moving row to row touches two entries however long the plan is.
  const hoveredFeatureId = useUIStore((s) => s.hoveredPlanFeatureId);
  const hoveredBundleId = useUIStore((s) => s.hoveredPlanBundleId);
  useEffect(() => {
    const entries = entriesRef.current;
    const next = new Set<string>();
    if (hoveredBundleId) {
      for (const [id, entry] of entries) if (entry.bundleId === hoveredBundleId) next.add(id);
    }
    if (hoveredFeatureId) next.add(hoveredFeatureId);
    const { off, on } = diffHoverIds(hoveredIdsRef.current, next);
    for (const id of off) {
      const entry = entries.get(id);
      if (entry) setHovered(entry, false);
    }
    for (const id of on) {
      const entry = entries.get(id);
      if (entry) setHovered(entry, true);
    }
    hoveredIdsRef.current = next;
  }, [hoveredFeatureId, hoveredBundleId]);

  // Bundle header click -> highlight every current member the same way a
  // single feature row does, above. Mutually exclusive with
  // selectedFeatureId in the store (each setter clears the other — see
  // uiStore.setSelectedPlanFeature/setSelectedPlanBundle), so this effect
  // and the one above never fight over the same entry.
  const selectedBundleId = useUIStore((s) => s.selectedPlanBundleId);
  const prevSelectedBundleRef = useRef<string | null>(null);
  useEffect(() => {
    const entries = entriesRef.current;
    const prevBundleId = prevSelectedBundleRef.current;
    if (prevBundleId && prevBundleId !== selectedBundleId) {
      for (const entry of entries.values()) {
        if (entry.bundleId !== prevBundleId) continue;
        setHighlight(entry, false);
        setBeaconSelected(entry, false);
      }
    }
    if (selectedBundleId) {
      for (const entry of entries.values()) {
        if (entry.bundleId !== selectedBundleId) continue;
        setHighlight(entry, true);
        setBeaconSelected(entry, true);
      }
    }
    prevSelectedBundleRef.current = selectedBundleId;
  }, [selectedBundleId]);

  // Panel row click -> fit the map to that one feature's bounds, consuming
  // the one-shot request the same way Map.web.tsx's own focusOnBounds does
  // for a whole plan (same padding/maxZoom so a single click-fit and a
  // whole-plan fit read the same). Scoped to `renderedPlan` rather than a
  // fresh Firestore lookup: a feature-level focus only ever fires for a
  // feature already on screen.
  const pendingFeatureFocus = useUIStore((s) => s.pendingPlanFeatureFocus);
  useEffect(() => {
    if (!pendingFeatureFocus) return;
    const map = mapRef.current;
    const feature = renderedPlan?.features.find((f) => f.id === pendingFeatureFocus);
    useUIStore.getState().clearPlanFeatureFocus();
    if (!map || !feature) return;
    const bounds = featureBounds(feature);
    map.fitBounds(
      [
        [bounds.s, bounds.w],
        [bounds.n, bounds.e],
      ],
      { ...planFitPadding(), maxZoom: 17 }
    );
  }, [pendingFeatureFocus, renderedPlan, mapRef]);

  // Bundle header click -> fit the map to every current member's combined
  // bounds, same request shape and padding as the single-feature focus
  // above.
  const pendingBundleFocus = useUIStore((s) => s.pendingPlanBundleFocus);
  useEffect(() => {
    if (!pendingBundleFocus) return;
    const map = mapRef.current;
    const members = renderedPlan?.features.filter((f) => f.bundleId === pendingBundleFocus) ?? [];
    useUIStore.getState().clearPlanBundleFocus();
    const bounds = planBounds(members);
    if (!map || !bounds) return;
    map.fitBounds(
      [
        [bounds.s, bounds.w],
        [bounds.n, bounds.e],
      ],
      { ...planFitPadding(), maxZoom: 17 }
    );
  }, [pendingBundleFocus, renderedPlan, mapRef]);
}
